/* Primitivi di disegno SVG — porting da flusso-urgenze.html
   (E/el 1085-1096, measure 675, textRuns 1098-1119, arrowHead 1122-1126,
   drawIcon 1176-1184, wrapInto 1220-1230). */
import { F_LAB, type Measure, type Run } from "@fullsteps/shared";
import { ICONS } from "./icons.js";

export const NS = "http://www.w3.org/2000/svg";

export type Attrs = Record<string, string | number | null | undefined>;

/** Nodo SVG con attributi (i null/undefined vengono ignorati). */
export function E(tag: string, attrs?: Attrs, txt?: string): SVGElement {
  const n = document.createElementNS(NS, tag);
  if (attrs) for (const k in attrs) if (attrs[k] != null) n.setAttribute(k, String(attrs[k]));
  if (txt != null) n.textContent = txt;
  return n;
}

/** Nodo HTML. */
export function el(tag: string, cls?: string, txt?: string): HTMLElement {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (txt != null) n.textContent = txt;
  return n;
}

/* Misura del testo via canvas 2d, con cache. Passata al motore per il layout. */
let _ctx: CanvasRenderingContext2D | null = null;
const _mcache = new Map<string, number>();
export const measureText: Measure = (txt, font) => {
  const k = font + "|" + txt;
  const hit = _mcache.get(k);
  if (hit !== undefined) return hit;
  if (!_ctx) _ctx = document.createElement("canvas").getContext("2d")!;
  _ctx.font = font;
  const w = _ctx.measureText(txt).width;
  _mcache.set(k, w);
  return w;
};
/** Svuota la cache (da chiamare quando i font finiscono di caricare). */
export function clearMeasureCache(): void {
  _mcache.clear();
}

export interface TextRunsOpts {
  cls?: string;
  anchor?: string;
  weight?: string | number;
  fill?: string;
  font?: string;
  max?: number;
}

/** <text> con run colorati; troncamento opzionale a `max` px. */
export function textRuns(x: number, y: number, runs: Run[], opts: TextRunsOpts = {}): SVGElement {
  const t = E("text", {
    x,
    y,
    class: opts.cls || "",
    "text-anchor": opts.anchor || "start",
    "font-weight": opts.weight ?? null,
    fill: opts.fill ?? null,
  });
  const font = opts.font || F_LAB;
  let used = 0;
  let cut = false;
  for (const r of runs) {
    if (cut) break;
    let s = r.text;
    if (opts.max != null) {
      const w = measureText(s, font);
      if (used + w > opts.max) {
        while (s.length && used + measureText(s + "…", font) > opts.max) s = s.slice(0, -1);
        s += "…";
        cut = true;
      }
      used += measureText(s, font);
    }
    if (!s) continue;
    t.appendChild(E("tspan", { fill: r.color || null }, s));
  }
  return t;
}

/** Testa di freccia leggermente concava. dir: +1 verso destra. */
export function arrowHead(x: number, y: number, dir: number, color: string): SVGElement {
  const d = 9.5;
  const w = 3.9;
  const bx = x - dir * d;
  return E("path", {
    d: `M${x},${y} L${bx},${y - w} Q${bx + dir * 2.6},${y} ${bx},${y + w} Z`,
    fill: color,
    "stroke-linejoin": "round",
  });
}

/** Icona Lucide scalata e colorata. */
export function drawIcon(
  name: string,
  cx: number,
  cy: number,
  size: number,
  color: string,
): SVGElement {
  const spec = ICONS[name] || ICONS["user"]!;
  const s = size / 24;
  const g = E("g", {
    transform: `translate(${cx - size / 2} ${cy - size / 2}) scale(${s})`,
    fill: "none",
    stroke: color,
    "stroke-width": 1.7,
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    opacity: 0.9,
  });
  for (const [tag, attrs] of spec) g.appendChild(E(tag, attrs));
  return g;
}

/** Testo a capo automatico in `maxLines` righe centrate verticalmente. */
export function wrapInto(
  g: SVGElement,
  str: string,
  x: number,
  y: number,
  maxw: number,
  cls: string,
  maxLines: number,
): void {
  const words = str.split(/[\s/]+/);
  const lines: string[] = [];
  let cur = "";
  const font = "13px 'Rethink Sans', system-ui, sans-serif";
  for (const w of words) {
    const t = cur ? cur + " " + w : w;
    if (measureText(t, font) > maxw && cur) {
      lines.push(cur);
      cur = w;
    } else cur = t;
  }
  if (cur) lines.push(cur);
  const use = lines.slice(0, maxLines);
  use.forEach((t, i) =>
    g.appendChild(E("text", { x, y: y + i * 14 - (use.length - 1) * 2, class: cls }, t)),
  );
}
